/**
 * `/api/agents/claims-lanes` — operator census of who holds what, for how
 * long, and which worktree lanes are alive (Phase 3.G / #46036).
 *
 * Read-only. Failure is payload, not an HTTP error — same shape as
 * `agent-health` / `verification-ops`. A missing vault or MCP CLI degrades to
 * `{ok:false, error}` so the page can render the probe failure as evidence.
 */

import { z } from "zod";
import type { Operation } from "../types";

export type ClaimLiveness =
  | "pid-alive"
  | "confirmed-dead"
  | "no-lease"
  | "unresponsive";

export type QuietClaimKind =
  | "abandoned-claim"
  | "parked-claim"
  | "rework-claim"
  | "stalled-claim";

export interface ClaimRowDto {
  taskId: number;
  title: string;
  status: string;
  holder: string | null;
  /** Latest `claim` transition, else lease `claimed_at`, else `updated_at`. */
  claimedAt: string | null;
  updatedAt: string;
  pid: number | null;
  liveness: ClaimLiveness;
  quietKind: QuietClaimKind | null;
  laneKey: string | null;
}

export interface LaneRowDto {
  key: string;
  kind: "worktree" | "zombie";
  path: string;
  branch: string;
  dirty: boolean;
  hasGitEntry: boolean;
  leased: boolean;
  pid: number | null;
  pidAlive: boolean | null;
  taskId: number | null;
}

export interface PoolStatusDto {
  size: number;
  leased: number;
  alive: number;
  dead: number;
  zombie: number[];
  stray: number[];
  free: number;
}

export interface GcLastDto {
  at: string | null;
  scanned: number | null;
  removed: string[];
  flaggedDirty: string[];
}

export interface ClaimsLanesViewDto {
  generatedAt: string;
  claims: ClaimRowDto[];
  lanes: LaneRowDto[];
  pool: PoolStatusDto | null;
  gcLast: GcLastDto | null;
  notes: string[];
}

export type ClaimsLanesResult =
  | { ok: true; status: ClaimsLanesViewDto }
  | { ok: false; error: string };

const getOp: Operation<void, void, void, ClaimsLanesResult> = {
  method: "GET",
  path: () => "/api/agents/claims-lanes",
  response: z.custom<ClaimsLanesResult>(),
};

export const claimsLanes = {
  get: getOp,
};
